import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { UserContext } from "../context/UserContext";

// `fullWidth` stretches the header to the page edges with the same side
// padding as the homepage hero, so the logo lines up with the hero title in
// the right corner. Other pages keep the centered max-width container.
const Navbar = ({ fullWidth = false }) => {
  const { user } = useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const navigate = useNavigate();

  // تشخیص اسکرول
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // جلوگیری از اسکرول بک‌گراند وقتی منو بازه
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (menuOpen || openModal) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [menuOpen, openModal]);

  useEffect(() => {
    if (!menuOpen && !openModal) return undefined;
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
        setOpenModal(false);
      }
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [menuOpen, openModal]);

  return (
    <>
      {/* ================= HEADER ================= */}
      <header
        className={`sticky top-0 z-40 border-b bg-white text-accent transition-all duration-300 ${isScrolled ? "border-gray-100 shadow-sm" : "border-transparent"
          }`}
      >
        <div
          className={`flex min-h-16 items-center justify-between ${fullWidth
            ? "w-full px-5 sm:px-10 lg:px-20"
            : "mx-auto max-w-7xl px-4 sm:px-6"
            }`}
        >

          {/* راست: لوگو + منو */}
          <div className="flex items-center gap-8 xl:gap-12">
            <Link to="/" className="flex items-center rounded-lg focus:outline-none focus:ring-4 focus:ring-primary/20" aria-label="صفحه اصلی">
              <img
                src="/images/mizban.png"
                alt="لوگو"
                className="h-auto w-24"
              />
            </Link>

            <nav className="hidden items-center gap-7 text-sm font-semibold lg:flex xl:gap-9" aria-label="ناوبری اصلی">
              <Link to="/services" className="rounded-md py-2 transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20">امکانات</Link>
              <Link to="/about" className="rounded-md py-2 transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20">درباره ما</Link>
              <Link to="/contact" className="rounded-md py-2 transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20">تماس با ما</Link>
              <Link to="/article" className="rounded-md py-2 transition-colors hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary/20">مقالات</Link>
            </nav>
          </div>

          {/* چپ: دکمه‌ها */}
          <div className="hidden items-center gap-3 lg:flex">
            <Link to="/restaurants" className="inline-flex min-h-10 items-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#e97116] focus:outline-none focus:ring-4 focus:ring-primary/20">
              میز رزرو کنید
            </Link>

            {user ? (
              <Link
                to="/dashboard"
                className="min-h-10 inline-flex items-center rounded-xl border border-secondary px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-secondary_1 focus:outline-none focus:ring-4 focus:ring-secondary/15"
              >
                پنل من
              </Link>
            ) : (
              <button
                onClick={() => setOpenModal(true)}
                className="min-h-10 rounded-xl border border-secondary px-4 py-2 text-sm font-semibold text-secondary transition-colors hover:bg-secondary_1 focus:outline-none focus:ring-4 focus:ring-secondary/15"
              >
                ورود | ثبت نام
              </button>
            )}
          </div>

          {/* همبرگری موبایل */}
          <button
            className="mr-auto rounded-lg p-2 text-primary transition-colors hover:bg-primary_1 focus:outline-none focus:ring-4 focus:ring-primary/20 lg:hidden"
            onClick={() => setMenuOpen(true)}
            aria-label="باز کردن منو"
            aria-expanded={menuOpen}
          >
            <Menu size={28} />
          </button>
        </div>
      </header>

      {/* ================= MOBILE MENU & BACKDROP ================= */}
      <AnimatePresence>
        {menuOpen && (
          <>
            {/* بک‌دراپ */}
            <motion.div
              className="fixed inset-0 bg-black/50 z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />

            {/* سایدبار */}
            <motion.div
              className="fixed top-0 left-0 h-full w-64 bg-light px-6 py-4 z-50 shadow-xl flex flex-col"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "tween", duration: 0.3 }}
            >
              <button
                className="mb-6 self-start rounded-lg p-2 text-2xl text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-4 focus:ring-primary/20"
                onClick={() => setMenuOpen(false)}
                aria-label="بستن منو"
              >
                <X />
              </button>

              <nav className="flex flex-col gap-3 text-sm font-bold text-accent">
                {[
                  ["/services", "خدمات"],
                  ["/about", "درباره ما"],
                  ["/contact", "تماس با ما"],
                  ["/article", "مقالات"],
                  ["/restaurants", "لیست رستوران‌ها"],
                ].map(([to, label]) => (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => setMenuOpen(false)}
                    className="py-2 border-b border-gray-200"
                  >
                    {label}
                  </Link>
                ))}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* ================= LOGIN MODAL ================= */}
      {openModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onMouseDown={() => setOpenModal(false)}>
          <div role="dialog" aria-modal="true" aria-labelledby="login-dialog-title" className="relative w-full max-w-sm space-y-5 rounded-2xl bg-white p-7 text-center shadow-2xl" onMouseDown={(event) => event.stopPropagation()}>
            <button type="button" onClick={() => setOpenModal(false)} className="absolute left-4 top-4 rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-4 focus:ring-primary/20" aria-label="بستن پنجره ورود">
              <X size={20} />
            </button>
            <h2 className="text-xl font-semibold text-gray-800">
              <span id="login-dialog-title">
              انتخاب نوع ورود
              </span>
            </h2>

            <button
              onClick={() => {
                setOpenModal(false);
                navigate("/auth/restaurant");
              }}
              className="w-full rounded-xl bg-secondary py-3 font-medium text-white transition-colors hover:bg-[#195a4c] focus:outline-none focus:ring-4 focus:ring-secondary/20"
            >
              ورود / ثبت‌نام رستوران
            </button>

            <button
              onClick={() => {
                setOpenModal(false);
                navigate("/auth/customer");
              }}
              className="w-full rounded-xl border border-gray-200 bg-white py-3 font-medium text-accent transition-colors hover:bg-gray-50 focus:outline-none focus:ring-4 focus:ring-gray-200"
            >
              ورود / ثبت‌نام مشتری
            </button>

            <button
              onClick={() => setOpenModal(false)}
              className="rounded-md px-2 py-1 text-xs text-gray-500 transition-colors hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
            >
              انصراف
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
